<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import aiReviewsAPI from 'dashboard/api/aiReviews';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import PageHeader from '../components/PageHeader.vue';
import { getApiErrorMessage, isPlainObject } from '../utils';

const { t } = useI18n();

const profiles = ref([]);
const selectedId = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const isReordering = ref(false);
const settingsError = ref('');
const form = ref({
  name: '',
  provider: 'openrouter',
  model_identifier: '',
  settings_json: '{}',
  enabled: true,
});

const selectedProfile = computed(() =>
  profiles.value.find(
    profile => Number(profile.id) === Number(selectedId.value)
  )
);
const isEditing = computed(() => Boolean(selectedProfile.value));

const resetForm = profile => {
  form.value = {
    name: profile?.name || '',
    provider: profile?.provider || 'openrouter',
    model_identifier: profile?.model_identifier || '',
    settings_json: JSON.stringify(profile?.settings_json || {}, null, 2),
    enabled: profile?.enabled ?? true,
  };
  settingsError.value = '';
};

watch(selectedProfile, profile => resetForm(profile));

const loadProfiles = async preferredId => {
  try {
    isLoading.value = true;
    const { data } = await aiReviewsAPI.getModelProfiles();
    profiles.value = data.payload || [];
    const exists = profiles.value.some(
      profile => Number(profile.id) === Number(preferredId)
    );
    selectedId.value = exists ? preferredId : profiles.value[0]?.id || null;
    if (!selectedId.value) resetForm();
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isLoading.value = false;
  }
};

// The saved order is the order of the model select on the new-batch page.
const onDragEnd = async ({ oldIndex, newIndex }) => {
  if (oldIndex === newIndex) return;

  try {
    isReordering.value = true;
    const { data } = await aiReviewsAPI.reorderModelProfiles(
      profiles.value.map(profile => profile.id)
    );
    profiles.value = data.payload || [];
    useAlert(t('AI_REVIEWS.MODEL_PROFILES.REORDERED'));
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
    await loadProfiles(selectedId.value);
  } finally {
    isReordering.value = false;
  }
};

const startNew = () => {
  selectedId.value = null;
  resetForm();
};

const parseSettings = () => {
  try {
    const settings = JSON.parse(form.value.settings_json);
    if (!isPlainObject(settings)) throw new Error();
    settingsError.value = '';
    return settings;
  } catch {
    settingsError.value = t('AI_REVIEWS.MODEL_PROFILES.JSON_ERROR');
    return null;
  }
};

const saveProfile = async () => {
  const settings = parseSettings();
  if (!settings) return;

  const attributes = {
    name: form.value.name,
    provider: 'openrouter',
    model_identifier: form.value.model_identifier,
    settings_json: settings,
    enabled: form.value.enabled,
  };

  try {
    isSaving.value = true;
    const { data } = isEditing.value
      ? await aiReviewsAPI.updateModelProfile(
          selectedProfile.value.id,
          attributes
        )
      : await aiReviewsAPI.createModelProfile(attributes);
    useAlert(
      t(
        isEditing.value
          ? 'AI_REVIEWS.MODEL_PROFILES.UPDATE_SUCCESS'
          : 'AI_REVIEWS.MODEL_PROFILES.CREATE_SUCCESS'
      )
    );
    await loadProfiles(data.id);
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => loadProfiles());
</script>

<template>
  <PageHeader
    :title="t('AI_REVIEWS.MODEL_PROFILES.TITLE')"
    :description="t('AI_REVIEWS.MODEL_PROFILES.DESCRIPTION')"
  >
    <template #actions>
      <Button
        :label="t('AI_REVIEWS.MODEL_PROFILES.NEW')"
        icon="i-lucide-plus"
        @click="startNew"
      />
    </template>
  </PageHeader>

  <div
    v-if="isLoading && !profiles.length"
    class="p-10 text-center text-n-slate-11"
  >
    {{ t('AI_REVIEWS.COMMON.LOADING') }}
  </div>
  <div v-else class="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
    <aside class="p-3 border rounded-xl border-n-weak bg-n-solid-2">
      <p
        v-if="!profiles.length"
        class="p-4 mb-0 text-sm text-center text-n-slate-11"
      >
        {{ t('AI_REVIEWS.MODEL_PROFILES.EMPTY') }}
      </p>
      <p
        v-if="profiles.length > 1"
        class="px-3 pt-1 pb-2 mb-0 text-xs text-n-slate-11"
      >
        {{ t('AI_REVIEWS.MODEL_PROFILES.DRAG_HINT') }}
      </p>
      <Draggable
        v-model="profiles"
        item-key="id"
        :disabled="isReordering"
        ghost-class="opacity-50"
        @end="onDragEnd"
      >
        <template #item="{ element: profile }">
          <button
            type="button"
            class="flex items-center w-full gap-2 p-3 text-left rounded-lg cursor-grab"
            :class="
              Number(selectedId) === Number(profile.id)
                ? 'bg-n-brand/10 text-n-brand'
                : 'text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="selectedId = profile.id"
          >
            <span
              class="i-lucide-grip-vertical size-4 shrink-0 text-n-slate-10"
            />
            <span class="flex-1 min-w-0">
              <span class="block text-sm font-medium truncate">{{
                profile.name
              }}</span>
              <span class="block text-xs truncate text-n-slate-11">
                {{ profile.model_identifier }}
              </span>
            </span>
            <span
              v-if="!profile.enabled"
              class="px-2 py-1 text-xs rounded-full bg-n-slate-3 text-n-slate-11"
            >
              {{ t('AI_REVIEWS.MODEL_PROFILES.DISABLED') }}
            </span>
          </button>
        </template>
      </Draggable>
    </aside>

    <form
      class="grid gap-5 p-5 border rounded-xl border-n-weak bg-n-solid-2"
      @submit.prevent="saveProfile"
    >
      <div class="grid gap-4 md:grid-cols-2">
        <Input
          id="ai-review-model-name"
          v-model="form.name"
          :label="t('AI_REVIEWS.MODEL_PROFILES.NAME')"
        />
        <Input
          id="ai-review-model-provider"
          v-model="form.provider"
          :label="t('AI_REVIEWS.MODEL_PROFILES.PROVIDER')"
          disabled
        />
      </div>
      <Input
        id="ai-review-model-identifier"
        v-model="form.model_identifier"
        :label="t('AI_REVIEWS.MODEL_PROFILES.MODEL_IDENTIFIER')"
      />
      <Textarea
        id="ai-review-model-settings"
        v-model="form.settings_json"
        :label="t('AI_REVIEWS.MODEL_PROFILES.SETTINGS_JSON')"
        :message="settingsError || t('AI_REVIEWS.MODEL_PROFILES.SETTINGS_HELP')"
        :message-type="settingsError ? 'error' : 'info'"
        resize
        custom-text-area-class="min-h-48 font-mono"
      />
      <label
        class="flex items-center justify-between gap-3 text-sm font-medium text-n-slate-12"
      >
        <span>{{ t('AI_REVIEWS.MODEL_PROFILES.ENABLED') }}</span>
        <Switch v-model="form.enabled" />
      </label>
      <div class="flex justify-end">
        <Button
          type="submit"
          :label="t('AI_REVIEWS.COMMON.SAVE')"
          :is-loading="isSaving"
          :disabled="
            !form.name.trim() || !form.model_identifier.trim() || isSaving
          "
        />
      </div>
    </form>
  </div>
</template>
